import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { resourceKind } from '@cotera/sdk/core';
import { cn, FOCUS_RING } from '@cotera/client/v0/lib/utils';
import { Assert } from '@cotera/utilities';
import type { ArtifactDescriptor, ArtifactOrigin } from './artifacts';
import { artifactTypeAsResourceType } from './artifacts';
import { ArtifactIcon } from './artifacts/artifact-type-icon';
import { useArtifactTitle } from './use-artifact-title';
import { artifactMatchesDirtyResource } from './artifact-dirty-state';
import { ArtifactDirtyDot } from './artifact-dirty-dot';
import { useDirtyArtifactResourceIds } from './use-dirty-artifact-resource-ids';
import { useArchivedArtifactIds } from './use-archived-artifact-ids';
import { useArtifactPanel } from './artifact-panel-context';
import { useArtifactsIndexVisibility } from './artifacts-index-visibility';
import { ARTIFACTS_INDEX_WIDTH } from './chat-layout';

/**
 * How many rows the card shows before it folds the rest behind "Show more".
 *
 * Five is what fits above the fold beside a conversation without the index
 * becoming the tallest thing in the pane — the *newest* five, see
 * {@link newestFirst} — past that the card starts competing
 * with the chat it is annotating, which is the opposite of what a standing
 * index is for. A chat that produced twenty artifacts is exactly the chat where
 * an unbounded list would be worst.
 */
const COLLAPSED_ROW_COUNT = 5;

/**
 * The list, newest end first.
 *
 * `Conversation.artifacts()` returns its merge order, not a chronology: the
 * parent chat's artifacts, then this chat's `load_artifact` actions, then its
 * tool outputs, then each sub-agent's. Dedupe is `map.set` on `artifactId`, so
 * an id keeps the position it was *first* seen at even when a much later tool
 * output supersedes it. Position therefore means "first seen, by source
 * priority" — and the parent's artifacts and the oldest registrations sort to
 * the front.
 *
 * That is fine for a list nothing is hidden from, and wrong the moment a cap
 * makes position load-bearing. At `slice(0, 5)` the card was showing the five
 * least recent things in the chat and folding everything the agent had just
 * made behind "Show more" — on the surface that is now the way back into a
 * panel that no longer opens by itself.
 *
 * Still a positional guess, not a sort: it reads the array's newest *end*, and
 * for a chat with sub-agents that end is the sub-agents rather than the latest
 * work. Ordering this honestly needs the `createdAt` that
 * `collectTimestampedArtifactDescriptorsFromToolMessages` already computes and
 * `collectArtifactDescriptorsFromToolMessages` discards.
 */
const newestFirst = (
  artifacts: readonly ArtifactDescriptor[]
): ArtifactDescriptor[] => [...artifacts].reverse();

/**
 * Artifact-only types have no {@link ResourceKindMeta} to read a label off, so
 * they are named here. Everything else defers to the resource kind, which is
 * where the app already spells "Document", "Agent", "Workflow" once.
 */
const ARTIFACT_ONLY_LABEL: Record<string, string> = {
  chart: 'Chart',
  'chart-dashboard': 'Dashboard',
  'workflow-node': 'Workflow step',
  folder: 'Folder',
};

/** Title-case, singular name of what an artifact is. */
export const artifactKindLabel = (artifactType: string): string => {
  const fromArtifactOnly = ARTIFACT_ONLY_LABEL[artifactType];
  if (fromArtifactOnly !== undefined) {
    return fromArtifactOnly;
  }
  const resourceType = artifactTypeAsResourceType(artifactType);
  if (resourceType !== null) {
    return resourceKind(resourceType).label;
  }
  if (artifactType.length === 0) {
    return 'Artifact';
  }
  return artifactType.charAt(0).toUpperCase() + artifactType.slice(1);
};

/**
 * The row's second line: what the chat did to this artifact, then what it is.
 *
 * The verb was hardcoded to "Created" and said so over everything the card
 * listed, including the three cases where it is plainly false — a workflow the
 * chat only opened, an agent it was handed by a mention, and an existing
 * resource it spent the whole conversation editing. Nothing on the row
 * contradicted it, so it read as a fact about provenance rather than the
 * decoration it was.
 *
 * {@link ArtifactDescriptor.origin} is that fact, worked out from the whole
 * transcript rather than from the one call that emitted the artifact. Where the
 * chat did neither — opened, ran, was given — the line is the kind alone. A
 * missing verb is a smaller loss than a wrong one: the kind is the part a mixed
 * list of charts, documents and workflows actually needs, and it was always
 * carrying the row.
 */
export const artifactSubtitle = (
  artifactType: string,
  origin: ArtifactOrigin | undefined
): string => {
  const kind = artifactKindLabel(artifactType);
  switch (origin) {
    case 'created':
      return `Created · ${kind}`;
    case 'edited':
      return `Edited · ${kind}`;
    // Two ways to have no verb, and they mean different things: `opened` is a
    // tool stating it changed nothing, `undefined` is nobody having said —
    // an artifact from a mention, or a chat recorded before tools declared it.
    case 'opened':
    case undefined:
      return kind;
    default:
      return Assert.unreachable(origin);
  }
};

/**
 * How many artifacts arrived while the card was folded shut.
 *
 * A new artifact does not open a collapsed Canvas (the auto-focus effect sets
 * the active tab but never expands a hidden panel), so the count can move with
 * nothing on screen to say it did. Opening the card is the acknowledgement —
 * once the list is up, everything on it has been seen.
 *
 * The baseline follows the count down as well as up: artifacts removed while
 * the card was shut must not strand it above the real count, which would
 * swallow the next genuine arrival.
 */
const useUnseenCount = (artifactCount: number, isOpen: boolean): number => {
  const [seenCount, setSeenCount] = useState(artifactCount);

  useEffect(() => {
    if (isOpen || artifactCount < seenCount) {
      setSeenCount(artifactCount);
    }
  }, [isOpen, artifactCount, seenCount]);

  return isOpen ? 0 : Math.max(0, artifactCount - seenCount);
};

/**
 * The row's secondary tier: the kind label and the type icon, everything that
 * is not the artifact's name.
 *
 * `muted-foreground` alone was not enough of a step down. In the v1 theme
 * `--foreground` is `oklch(0.3602 …)` and `--muted-foreground` is
 * `oklch(0.438 …)` — under 8 points of lightness apart, so a title and its kind
 * label landed as nearly the same weight of ink and the row read flat. Notion's
 * equivalent pair is roughly L 30% against L 62%; the hierarchy there is doing
 * most of the work of telling you what to read first.
 *
 * At 80% over the card's near-white ground this composites to about L 55%,
 * roughly tripling the separation, and it is the step this codebase already
 * takes for small metadata (the web-search and site-scraper renderers use the
 * same `text-xs text-muted-foreground/80`). It is not a new colour — widening
 * the gap by darkening the title instead would need one, since the v1 theme has
 * nothing below `--foreground`.
 *
 * The cost is contrast: ~3.9:1 against the card, under the 4.5:1 AA wants for
 * body text, where full `muted-foreground` sits at ~5.5:1. That is a deliberate
 * trade on a 12px label that repeats the same two words down the list, and it is
 * still more conservative than the ~3:1 of the design it is following. `/90`
 * (~4.8:1) is the AA-safe setting if the tier should stay compliant; it keeps
 * about half the separation.
 */
const RECESSED = 'text-muted-foreground/80';

/**
 * One artifact: icon, live title, "Edited · Document", dirty dot.
 *
 * The whole row is the button — there is no per-row remove here. Removing an
 * artifact from the chat is the switcher menu's job, in the panel's own bar;
 * this card is the way *back* into a panel that was put away, and a destructive
 * control on every row of it would be the easiest thing to hit by accident.
 *
 * No "current" row either. The card only exists while the Canvas is away, so
 * nothing here is open — and `activeArtifactId` is whichever tab the panel
 * *would* open, which since the panel stopped opening by itself is just the last
 * artifact the chat produced. Marking it read as "you are here" for a place
 * nobody had been.
 */
const ArtifactIndexRow: React.FC<{
  artifact: ArtifactDescriptor;
  isDirty: boolean;
  onSelect: (artifactId: string) => void;
}> = ({ artifact, isDirty, onSelect }) => {
  const title = useArtifactTitle(artifact);

  return (
    <button
      type="button"
      onClick={() => onSelect(artifact.artifactId)}
      aria-label={`Open ${title}`}
      className={cn(
        'flex w-full min-w-0 cursor-pointer items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/60',
        FOCUS_RING
      )}
    >
      {/* `regular` (16px), matching the title beside it — `text-sm` is 1rem in
            the v1 theme, so a 12px `small` icon sat visibly under its own
            label. `mt-0.5` centres it on the first line rather than the whole
            two-line block.

            Recessed to the subtitle's tier rather than left at full
            `muted-foreground`, which would have made the glyph darker than the
            label directly beside it — see {@link RECESSED} on why the row wants
            exactly two levels of ink. */}
      <ArtifactIcon
        artifactType={artifact.type}
        className={cn('mt-0.5 shrink-0', RECESSED)}
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          {/* `font-medium`, as the inline artifact card in chat
                (`ArtifactSnippet`) has always had on the same pair. This row
                was the one place that dropped it, so the name carried no more
                emphasis than the kind label under it. */}
          <span
            className="truncate text-sm font-medium leading-5 text-foreground"
            title={title}
          >
            {title}
          </span>
          {isDirty ? <ArtifactDirtyDot /> : null}
        </span>
        {/* Notion's second line names where the thing came from. Ours names
              what this chat did to it and what it is — see
              {@link artifactSubtitle}. */}
        <span className={cn('truncate text-xs leading-4', RECESSED)}>
          {artifactSubtitle(artifact.type, artifact.origin)}
        </span>
      </span>
    </button>
  );
};

/**
 * How long the card takes to fold, and on what curve.
 *
 * The curve matters more than the number here. The Canvas's own width animation
 * ({@link ArtifactSidePanel}) is `duration-[320ms] ease-out`, and matching it
 * was the obvious move — but `ease-out` is `cubic-bezier(0, 0, 0.2, 1)`, which
 * is 41% through the travel at a *seventh* of its duration and 84% through at
 * the halfway mark. On a panel sliding several hundred pixels that front-loading
 * reads as decisive. On a fold of ~190px it reads as a snap followed by a settle
 * too small to see, which is to say it reads as no transition at all.
 *
 * `ease-in-out` spends its first fifth easing in (7% of the travel where
 * ease-out has already spent 41%), so the fold has a beginning the eye can
 * catch. 400ms rather than 320ms because a curve with a slow start needs the
 * room to use it — at 320ms the eased-in portion is over before it registers.
 */
const FOLD_TRANSITION =
  'duration-[400ms] ease-in-out motion-reduce:transition-none';

/**
 * The card's header row: the "Artifacts" label, whatever the state hangs on the
 * right, the chevron, and the whole row as the toggle.
 *
 * The whole row, not the chevron alone — shutting the list is the same gesture
 * as opening it, and a 24px target for the way back out of a control you
 * reached with a 352px one is the asymmetry, not the fix. The chevron stays as
 * the affordance.
 *
 * One button across both states rather than two swapped at the toggle: the two
 * had byte-identical metrics and label and differed only in the handler and the
 * chevron, and any drift between them showed as the card twitching mid-fold.
 * Keeping one also means the chevron can *rotate* between the states instead of
 * being replaced, which is what makes the toggle read as a single motion rather
 * than a swap that happens to be next to an animation.
 */
const CardHeaderButton: React.FC<{
  onClick: () => void;
  ariaLabel: string;
  isOpen: boolean;
  children?: React.ReactNode;
}> = ({ onClick, ariaLabel, isOpen, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    aria-expanded={isOpen}
    className={cn(
      'flex w-full cursor-pointer items-center gap-2 px-4 py-2.5 text-left',
      FOCUS_RING
    )}
  >
    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
      Artifacts
    </span>
    {children}
    <ChevronRight
      className={cn(
        'size-4 shrink-0 text-muted-foreground/70 transition-transform',
        FOLD_TRANSITION,
        isOpen && 'rotate-90'
      )}
    />
  </button>
);

export type ArtifactsIndexCardProps = {
  artifacts: readonly ArtifactDescriptor[];
  /** Open an artifact in the Canvas (expanding it when collapsed). */
  onSelect: (artifactId: string) => void;
  className?: string;
};

/**
 * The way back into a Canvas that was put away with artifacts still in it: a
 * card in the pane's top-right listing each one by icon, live title and kind,
 * folding shut to an `Artifacts N ›` pill.
 *
 * This replaces a bare icon-and-count button in the same corner. That control
 * could say *how many* artifacts were waiting but never *which*, and it was the
 * only route back to them — so the answer to "did the agent make the thing I
 * asked for" was to reopen the panel onto whichever artifact happened to be
 * focused last and switch from there. The list names them and makes each one a
 * single click, which is what the button cost anyway.
 *
 * It exists only while the Canvas is away. With the panel up, the artifact's
 * own bar already carries the switcher across everything open; a second index
 * floating over the chat would answer the same question at the cost of covering
 * the conversation. So the card appears when the panel goes, opens itself
 * (there is nothing else pointing at the artifacts), and is gone the moment one
 * is picked.
 *
 * Desktop only. Mobile has no pane to float over — its artifacts live in the
 * pill row above the tabs.
 */
export const ArtifactsIndexCard: React.FC<ArtifactsIndexCardProps> = ({
  artifacts,
  onSelect,
  className,
}) => {
  const artifactPanel = useArtifactPanel();
  const isCanvasHidden = artifactPanel?.isHidden === true;
  const dirtyResourceIds = useDirtyArtifactResourceIds();
  const visibility = useArtifactsIndexVisibility();
  const archivedArtifactIds = useArchivedArtifactIds();

  /**
   * Archived artifacts are gone from here outright — not greyed, not folded
   * behind "Show more".
   *
   * This card is a standing index of what the chat currently has, and the count
   * on its closed pill is the promise it makes about that. An artifact the user
   * archived is one they have said they are done with; leaving it on the list
   * means the way back into the Canvas keeps offering a row that opens onto
   * "this dataset is archived", and the unseen-count baseline keeps moving for
   * things nobody will look at.
   *
   * Every derived number below reads this rather than `artifacts`, so the
   * count, the cap and the unseen badge all describe the list actually on
   * screen.
   */
  const liveArtifacts = useMemo(
    () =>
      archivedArtifactIds.size === 0
        ? artifacts
        : artifacts.filter(
            (artifact) => !archivedArtifactIds.has(artifact.artifactId)
          ),
    [artifacts, archivedArtifactIds]
  );

  // The card is the only place that knows both halves of "there is something to
  // toggle", so it publishes the answer for the chat header's control. Reported
  // from an effect rather than during render because it writes to a provider
  // above this one; `setCanShow` bails out on an unchanged value, so a steady
  // state settles after one pass rather than looping.
  //
  // Reads `liveArtifacts`, like every other derived value here. A chat whose
  // artifacts have all been archived has nothing to index, and offering a
  // header control that toggles an empty pane would be worse than offering none.
  //
  // The cleanup matters as much as the report. The card lives inside the
  // desktop split; on a narrow viewport the panel renders its mobile tabs
  // instead and this never mounts at all. Without the cleanup, a window
  // narrowed past that breakpoint — or a phone rotated to portrait — unmounted
  // the card with its last `true` still standing, leaving a "Hide artifacts"
  // button in the chat header that toggled a card no longer on the page.
  const reportCanShow = visibility?.reportCanShow;
  const canShow = liveArtifacts.length > 0 && isCanvasHidden;
  useEffect(() => {
    reportCanShow?.(canShow);
    return () => {
      reportCanShow?.(false);
    };
  }, [reportCanShow, canShow]);

  const [isOpen, setIsOpen] = useState(isCanvasHidden);
  const [showAll, setShowAll] = useState(false);
  const wasCanvasHiddenRef = useRef(isCanvasHidden);

  useEffect(() => {
    if (wasCanvasHiddenRef.current === isCanvasHidden) {
      return;
    }
    wasCanvasHiddenRef.current = isCanvasHidden;
    setIsOpen(isCanvasHidden);
  }, [isCanvasHidden]);

  const unseenCount = useUnseenCount(liveArtifacts.length, isOpen);

  // "Show more" is about this list as it stands. A chat that drops back under
  // the cap has nothing left to expand, and leaving the flag set would show
  // "Show less" over a list that is already whole.
  useEffect(() => {
    if (liveArtifacts.length <= COLLAPSED_ROW_COUNT) {
      setShowAll(false);
    }
  }, [liveArtifacts.length]);

  const visibleArtifacts = useMemo(() => {
    const ordered = newestFirst(liveArtifacts);
    return showAll ? ordered : ordered.slice(0, COLLAPSED_ROW_COUNT);
  }, [liveArtifacts, showAll]);

  // Nothing to index, or the Canvas is on screen and already carries the
  // switcher in the artifact's own bar. A chat whose every artifact has been
  // archived takes this branch too, so the card goes rather than standing empty
  // over the conversation.
  if (!canShow) {
    return null;
  }

  // Dismissed from the chat header. Distinct from the folded state below, which
  // leaves the `Artifacts N ›` pill in the corner — this leaves the pane clear,
  // and the header's toggle is the only way back.
  if (visibility !== null && !visibility.isVisible) {
    return null;
  }

  const hiddenRowCount = liveArtifacts.length - visibleArtifacts.length;
  const countLabel =
    liveArtifacts.length === 1
      ? '1 artifact'
      : `${liveArtifacts.length} artifacts`;
  const openLabel =
    unseenCount > 0
      ? `Show ${countLabel} (${unseenCount} new)`
      : `Show ${countLabel}`;

  return (
    <div
      data-slot="artifacts-index-card"
      className={cn(
        // Same corner the Canvas's own hide button occupies when the panel is
        // open, so putting the artifacts away and getting them back happen in
        // one place.
        //
        // `bg-card`, like the Canvas it stands in for and like every other
        // floating surface in this theme. At `bg-background` it was the page's
        // own tone laid over the page, so only its border held it off the
        // conversation — a card that reads as a hole cut in the pane rather than
        // something resting on it.
        'absolute right-3 top-3 z-10 overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm',
        ARTIFACTS_INDEX_WIDTH,
        className
      )}
    >
      <CardHeaderButton
        onClick={() => setIsOpen((previous) => !previous)}
        ariaLabel={isOpen ? 'Hide artifacts list' : openLabel}
        isOpen={isOpen}
      >
        {isOpen ? null : (
          <span className="relative shrink-0 text-sm tabular-nums text-muted-foreground">
            {liveArtifacts.length}
            {unseenCount > 0 ? (
              // The app's unread mark, same as the chat and library rows: a 2x2
              // primary dot ringed in the card colour. A dot waits to be noticed
              // where a flash would have to be caught.
              <span
                aria-hidden="true"
                className="absolute -right-1.5 -top-0.5 size-2 rounded-full bg-primary ring-2 ring-card"
              />
            ) : null}
          </span>
        )}
      </CardHeaderButton>
      {/* The list stays mounted through the fold — nothing can animate its own
          unmount — and collapses on `grid-template-rows` from `1fr` to `0fr`.
          That is the one way to transition to a content height nobody has
          measured: the track resolves to the rows' natural height at `1fr`, and
          the browser interpolates the track, so the card never needs a pixel
          number that would go stale the moment a title wrapped to two lines.

          `invisible` and `aria-hidden` are what keep "mounted" from meaning
          "reachable": a folded card must not hand its rows to Tab or to a
          screen reader just because they are still in the DOM waiting to be
          clipped. `visibility` is in the transition list on purpose — CSS holds
          it at `visible` for the whole of any transition that touches it, so it
          flips only once the fold has finished rather than blanking the rows on
          the first frame of the close. (`inert` would say this in one word, but
          the repo is on `@types/react` 18, which predates the attribute.)

          The inner `overflow-hidden` does the clipping and, being a grid item
          that is not `visible`, is also what lets the track reach zero at
          all. */}
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity,visibility]',
          FOLD_TRANSITION,
          isOpen
            ? 'visible grid-rows-[1fr] opacity-100'
            : 'invisible grid-rows-[0fr] opacity-0'
        )}
        aria-hidden={!isOpen}
      >
        <div className="overflow-hidden">
          {/* `px-2` here plus `px-2` on each row puts the titles 16px off the
              card's edge — the same inset as the header's `px-4` above, so the
              label and the names under it share a left edge. Change one and the
              other has to follow. */}
          <div className="max-h-[60dvh] overflow-y-auto px-2 pb-2">
            {visibleArtifacts.map((artifact) => (
              <ArtifactIndexRow
                key={artifact.artifactId}
                artifact={artifact}
                isDirty={artifactMatchesDirtyResource(
                  artifact,
                  dirtyResourceIds
                )}
                onSelect={onSelect}
              />
            ))}
            {hiddenRowCount > 0 || showAll ? (
              <button
                type="button"
                onClick={() => setShowAll((previous) => !previous)}
                className={cn(
                  // `text-xs`, matching the rows' second line rather than their
                  // titles. At `text-sm` this sat at the same weight as the
                  // artifact names above it, so the way to see *more* names read
                  // as one more name in the list.
                  'mt-0.5 w-full cursor-pointer rounded-lg px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground',
                  FOCUS_RING
                )}
              >
                {showAll ? 'Show less' : 'Show more'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
